import type { Profile } from '../types/database';



/** User is "Active now" if last heartbeat is within this window. */

export const PRESENCE_ONLINE_MS = 3 * 60 * 1000;



export type PresenceStatus = 'active' | 'open_to_chat' | 'busy' | 'away' | 'offline' | 'hidden';



export type ResolvedPresence = {

  isActiveNow: boolean;

  status: PresenceStatus;

  /** Display line for chat / profile (e.g. "Active now", "Last seen 3h ago") */

  label: string;

};



export function parseLastSeenMs(lastSeenAt: string | null | undefined): number | null {

  if (!lastSeenAt) return null;

  const ms = new Date(lastSeenAt).getTime();

  return Number.isFinite(ms) ? ms : null;

}



/**

 * Relative time since last activity: minutes, then hours (<24h), then days.

 * Does not return "Active now" — use resolvePresence for live status.

 */

export function formatLastSeenRelative(lastSeenMs: number | null): string {

  if (lastSeenMs == null) return 'a while ago';

  const diff = Math.max(0, Date.now() - lastSeenMs);

  const mins = Math.floor(diff / 60_000);

  if (mins < 1) return 'just now';

  if (mins < 60) return `${mins}m ago`;

  const hours = Math.floor(mins / 60);

  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);

  if (days < 7) return `${days}d ago`;

  return new Date(lastSeenMs).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

}



export function formatLastSeenLabel(lastSeenMs: number | null): string {

  if (lastSeenMs == null) return 'Offline';

  const rel = formatLastSeenRelative(lastSeenMs);

  if (rel === 'just now') return 'Last seen just now';

  if (rel === 'a while ago') return 'Offline';

  return `Last seen ${rel}`;

}



type PresenceInput = {

  lastSeenAt?: string | null;

  availabilityStatus?: Profile['availability_status'] | string | null;

  showActivity?: boolean;

};



/** Derive chat/profile presence from heartbeat + manual availability + privacy. */

export function resolvePresence(input: PresenceInput): ResolvedPresence {

  const showActivity = input.showActivity !== false;

  const avail = (input.availabilityStatus || 'offline') as Profile['availability_status'];

  const lastMs = parseLastSeenMs(input.lastSeenAt);

  const recentlyActive = lastMs != null && Date.now() - lastMs < PRESENCE_ONLINE_MS;



  if (!showActivity) {

    return { isActiveNow: false, status: 'hidden', label: 'Offline' };

  }



  if (recentlyActive && avail === 'open_to_chat') {

    return { isActiveNow: true, status: 'open_to_chat', label: 'Open to chat' };

  }



  if (recentlyActive && avail === 'busy') {

    return { isActiveNow: false, status: 'busy', label: 'Busy' };

  }



  if (recentlyActive && avail === 'away') {

    return { isActiveNow: false, status: 'away', label: 'Away' };

  }



  if (recentlyActive && avail === 'online') {

    return { isActiveNow: true, status: 'active', label: 'Active now' };

  }



  if (lastMs != null) {

    return {

      isActiveNow: false,

      status: 'offline',

      label: formatLastSeenLabel(lastMs),

    };

  }



  return { isActiveNow: false, status: 'offline', label: 'Offline' };

}



export function presenceFromProfile(profile: Partial<Profile> | null | undefined): ResolvedPresence {

  if (!profile) {

    return { isActiveNow: false, status: 'offline', label: 'Offline' };

  }

  return resolvePresence({

    lastSeenAt: profile.last_seen_at,

    availabilityStatus: profile.availability_status,

    showActivity: profile.prefs_show_activity !== false,

  });

}



export function presenceDotClass(status: PresenceStatus, isActiveNow: boolean): string {

  if (isActiveNow) return 'bg-green-500';

  if (status === 'open_to_chat') return 'bg-blue-500';

  if (status === 'busy') return 'bg-yellow-500';

  if (status === 'away') return 'bg-orange-500';

  return 'bg-gray-500';

}


